/**
 * SubscriptionPlans Component
 *
 * Displays available subscription plans with upgrade/downgrade options
 * Integrates with Stripe Checkout and Customer Portal
 */

'use client';

import { FC, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter, usePathname } from 'next/navigation';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { PrimaryButton, SecondaryButton } from '@/src/shared/ui/button';
import { Check, Crown, Zap, Star, Loader2, ExternalLink } from 'lucide-react';
import { subscriptionsApi } from '@/src/shared/api/subscriptions.api';
import { toast } from 'sonner';

interface SubscriptionPlansProps {
  currentTier: 'FREE' | 'STANDARD' | 'PROFESSIONAL' | 'ADVANCED';
  currentStatus: string;
  hasStripeCustomer?: boolean;
}

interface Plan {
  id: 'FREE' | 'STANDARD' | 'PROFESSIONAL' | 'ADVANCED';
  name: string;
  description: string;
  price: string;
  priceValue: number;
  icon: typeof Crown;
  color: string;
  bgColor: string;
  borderColor: string;
  features: string[];
}

export const SubscriptionPlans: FC<SubscriptionPlansProps> = ({
  currentTier,
  currentStatus,
  hasStripeCustomer = false,
}) => {
  const t = useTranslations('profile.subscription');
  const router = useRouter();
  const pathname = usePathname();
  const locale = pathname.split('/')[1];

  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);

  const plans: Plan[] = [
    {
      id: 'FREE',
      name: t('tiers.FREE.name'),
      description: t('tiers.FREE.description'),
      price: '$0',
      priceValue: 0,
      icon: Check,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      features: [
        '2 active proposals',
        '3 categories',
        '5 portfolio items',
        'Basic profile',
      ],
    },
    {
      id: 'STANDARD',
      name: t('tiers.STANDARD.name'),
      description: t('tiers.STANDARD.description'),
      price: '$29',
      priceValue: 29,
      icon: Zap,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      features: [
        '10 active proposals',
        '5 categories',
        '10 portfolio items',
        'Priority support',
      ],
    },
    {
      id: 'PROFESSIONAL',
      name: t('tiers.PROFESSIONAL.name'),
      description: t('tiers.PROFESSIONAL.description'),
      price: '$79',
      priceValue: 79,
      icon: Star,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      features: [
        '30 active proposals',
        '10 categories',
        '20 portfolio items',
        'Priority listing',
        'Analytics',
        'Featured badge',
      ],
    },
    {
      id: 'ADVANCED',
      name: t('tiers.ADVANCED.name'),
      description: t('tiers.ADVANCED.description'),
      price: '$149',
      priceValue: 149,
      icon: Crown,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      features: [
        'Unlimited proposals',
        'Unlimited categories',
        'Unlimited portfolio',
        'Top priority listing',
        'Advanced analytics',
        'Dedicated support',
      ],
    },
  ];

  const currentPlanIndex = plans.findIndex((p) => p.id === currentTier);
  const currentPlan = plans[currentPlanIndex];

  const handleSelectPlan = async (planId: string) => {
    if (planId === 'FREE') {
      toast.info(t('cannotDowngradeToFree'));
      return;
    }

    if (planId === currentTier) {
      toast.info(t('alreadyOnThisPlan'));
      return;
    }

    try {
      setLoadingTier(planId);

      const { url } = await subscriptionsApi.createCheckoutSession(
        planId,
        `${window.location.origin}/${locale}/subscription/success`,
        `${window.location.origin}/${locale}/subscription/cancel`,
      );

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      toast.error(error instanceof Error ? error.message : t('errors.checkoutFailed'));
      setLoadingTier(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!hasStripeCustomer) {
      toast.error(t('errors.noStripeCustomer'));
      return;
    }

    try {
      setOpeningPortal(true);
      const { url } = await subscriptionsApi.getCustomerPortalUrl(
        `${window.location.origin}/${locale}/profile?tab=settings`,
      );

      // Open Stripe Customer Portal
      window.location.href = url;
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      toast.error(error instanceof Error ? error.message : t('errors.portalFailed'));
      setOpeningPortal(false);
    }
  };

  const getActionButton = (plan: Plan) => {
    const isLoading = loadingTier === plan.id;
    const isCurrent = plan.id === currentTier;
    const isUpgrade = plan.priceValue > currentPlan?.priceValue;
    const isDowngrade = plan.priceValue < currentPlan?.priceValue && plan.id !== 'FREE';

    if (isCurrent) {
      return (
        <SecondaryButton className="w-full" disabled>
          {t('currentPlan')}
        </SecondaryButton>
      );
    }

    if (plan.id === 'FREE') {
      return null;
    }

    return (
      <PrimaryButton
        className={`w-full ${isDowngrade ? 'bg-gray-600 hover:bg-gray-700' : ''}`}
        onClick={() => handleSelectPlan(plan.id)}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            {t('loading')}
          </>
        ) : isUpgrade ? (
          t('upgrade')
        ) : (
          t('downgrade')
        )}
      </PrimaryButton>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Typography variant="h3" className="mb-2">
          {t('availablePlans')}
        </Typography>
        <Typography variant="bodySm" className="text-text-secondary">
          {t('choosePlanDescription')}
        </Typography>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((plan) => {
          const Icon = plan.icon;
          const isCurrent = plan.id === currentTier;

          return (
            <div
              key={plan.id}
              className={`relative p-5 rounded-lg border-2 transition-all ${
                isCurrent
                  ? `${plan.borderColor} bg-gradient-to-br ${plan.bgColor} to-white shadow-md`
                  : 'border-border-primary hover:border-accent-primary hover:shadow-sm'
              }`}
            >
              {/* Current Badge */}
              {isCurrent && (
                <div className="absolute -top-2 -right-2">
                  <span className="bg-accent-primary text-white px-2 py-1 rounded-full text-xs font-semibold">
                    {t('current')}
                  </span>
                </div>
              )}

              {/* Icon */}
              <div className={`p-2 rounded-lg ${plan.bgColor} w-fit mb-3`}>
                <Icon className={`w-5 h-5 ${plan.color}`} />
              </div>

              {/* Plan Name */}
              <Typography variant="h4" className="mb-1">
                {plan.name}
              </Typography>

              {/* Description */}
              <Typography variant="bodySm" className="text-text-secondary mb-3 h-10">
                {plan.description}
              </Typography>

              {/* Price */}
              <div className="mb-4">
                <div className="flex items-baseline">
                  <Typography variant="h3" className={plan.color}>
                    {plan.price}
                  </Typography>
                  {plan.priceValue > 0 && (
                    <Typography variant="bodySm" className="text-text-secondary ml-1">
                      /mo
                    </Typography>
                  )}
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-2 mb-4">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-success-primary flex-shrink-0 mt-0.5" />
                    <Typography variant="bodySm" className="text-text-secondary">
                      {feature}
                    </Typography>
                  </li>
                ))}
              </ul>

              {/* Action Button */}
              {getActionButton(plan)}
            </div>
          );
        })}
      </div>

      {/* Manage Subscription (for existing customers) */}
      {hasStripeCustomer && currentTier !== 'FREE' && (
        <div className="p-4 bg-background-secondary rounded-lg border border-border-primary">
          <div className="flex items-center justify-between">
            <div>
              <Typography variant="body" className="font-semibold mb-1">
                {t('manageYourSubscription')}
              </Typography>
              <Typography variant="bodySm" className="text-text-secondary">
                {t('manageSubscriptionDescription')}
              </Typography>
            </div>
            <SecondaryButton
              onClick={handleManageSubscription}
              disabled={openingPortal}
              className="flex items-center gap-2"
            >
              {openingPortal ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('loading')}
                </>
              ) : (
                <>
                  {t('manageSubscription')}
                  <ExternalLink className="w-4 h-4" />
                </>
              )}
            </SecondaryButton>
          </div>
        </div>
      )}

      {/* Info Notice */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <Typography variant="bodySm" className="text-blue-900">
          <strong>{t('note')}:</strong> {t('billingNote')}
        </Typography>
      </div>
    </div>
  );
};
